import type { CoverageState, KpiSummary } from './processes.api';

/**
 * Of een proces helemaal geen procesdata heeft: 'unmapped' (de bron kent dit proces niet — vier van
 * de acht diensten, meting 2026-08-14) of 'no_data' (het proces bestaat, maar nog zonder events).
 * Eén gedeelde afleiding voor élke plek die daarop reageert — de lege staat op de kaart, de lege
 * staat op de detailpagina, en het onderdrukken van de ordergebaseerde doorlooptijd. Zonder die
 * deling drijven de plekken uit elkaar zodra er een coverageState bijkomt.
 */
export function hasNoProcessData(coverageState: CoverageState): boolean {
  return coverageState === 'no_data' || coverageState === 'unmapped';
}

/**
 * Derives a single, display-oriented aggregate `data-kpi-state` from a `KpiSummary` (AD-24: one
 * shared derivation, consumed by both the landing tegel and the proces-detail KPI block — never
 * a second per-component interpretation). `null` when there is no order data at all (total=0,
 * i.e. still a placeholder).
 */
export function aggregateKpiDataState(kpiSummary: KpiSummary): 'not_started' | 'on_time' | 'over_kpi' | 'stuck' | null {
  if (kpiSummary.total === 0) return null;
  if (kpiSummary.meanWorkdays == null) return 'not_started';
  if (kpiSummary.stuckCount > 0) return 'stuck';
  if (kpiSummary.overKpiCount > 0) return 'over_kpi';
  return 'on_time';
}

/** Locale-correct "bijgewerkt {{time}}" formatting for the ISO 8601 UTC `fetchedAt` timestamp. */
export function formatFetchedAt(isoDate: string, locale: string): string {
  const date = new Date(isoDate);
  const datePart = date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const timePart = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return `${datePart} ${timePart}`;
}

/** Rounds a percentage for display (e.g. KPI-attainment) without implying false precision. */
export function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** Rounds a workdays figure for display; whole numbers read as e.g. "2" not "2.0". */
export function formatWorkdays(value: number): string {
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

/** Het pad naar de stapvertalingen; geen zichtbare tekst, dus buiten `t()` gehouden. */
const STEP_TRANSLATION_PREFIX = 'processes.steps.';

/**
 * De stapnaam in gewone taal. De bron levert samengestelde codes (`INTAKE/READYFORPROCESSING`,
 * `PHYSICAL/MEASURED`) en die hoorden nooit op het scherm te staan; de vertaaltabel komt uit de
 * mockups. Kent de tabel een code niet — de bron voegt er ooit een toe — dan blijft de ruwe code
 * staan. Dat is lelijk maar eerlijk, en beter dan een lege cel.
 *
 * Eén afleiding voor élke plek die een stap toont: detailscherm, dekking, portfolio, stap-itemlijst
 * en de tijden per stap-overgang. Zonder die deling drijven ze uit elkaar zodra de bron verandert.
 */
export function stepLabel(canonicalStep: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  return t(stepLabelKey(canonicalStep), { defaultValue: canonicalStep });
}

/** De vertaalsleutel achter `stepLabel`, ook gebruikt door het kruimelpad. */
export function stepLabelKey(canonicalStep: string): string {
  return STEP_TRANSLATION_PREFIX + canonicalStep.toLowerCase().replace(/\//g, '_');
}
